import { OfficeHolidayConfig } from '@/types/office';
import { format, getDay, getDate, startOfMonth, addDays, isSameDay } from 'date-fns';

/**
 * Get the day name as lowercase string (0=sunday, 1=monday, ..., 6=saturday)
 */
const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;

/**
 * Check if a given date falls on the Nth occurrence of a specific weekday in its month.
 * e.g., isNthWeekdayOfMonth(date, 6, 2) → is it the 2nd Saturday?
 */
function getNthWeekdayOccurrence(date: Date, targetDay: number): number {
  const monthStart = startOfMonth(date);
  let count = 0;
  let d = new Date(monthStart);

  while (d <= date) {
    if (getDay(d) === targetDay) {
      count++;
    }
    d = addDays(d, 1);
  }

  return count;
}

/**
 * Check if a date is a holiday according to the office holiday configuration.
 * Returns { isHoliday: boolean, reason?: string }
 */
export function isHoliday(
  date: Date,
  config?: OfficeHolidayConfig,
  excludeCurrentDay?: boolean
): { isHoliday: boolean; reason?: string } {
  if (!config || !config.enabled) {
    return { isHoliday: false };
  }

  const dateStr = format(date, 'yyyy-MM-dd');
  const dayOfWeek = getDay(date); // 0=Sun, 6=Sat
  const dayName = DAY_NAMES[dayOfWeek];

  // 1. Check specific holiday dates (always applies, even if excludeCurrentDay)
  if (config.holidayDates?.includes(dateStr)) {
    return { isHoliday: true, reason: 'Holiday' };
  }

  // If excludeCurrentDay is true and date is today, skip weeklyOff + Saturday checks.
  // This means a newly-set weeklyOff or Saturday-off will NOT retroactively mark today as holiday.
  if (excludeCurrentDay && isSameDay(date, new Date())) {
    return { isHoliday: false };
  }

  // 2. Check weekly off
  if (config.weeklyOff && dayName === config.weeklyOff) {
    return { isHoliday: true, reason: `Weekly Off (${capitalize(config.weeklyOff)})` };
  }

  // 3. Check 2nd/4th Saturday
  if (dayOfWeek === 6) { // Saturday
    const occurrence = getNthWeekdayOccurrence(date, 6);
    if (config.secondSaturdayOff && occurrence === 2) {
      return { isHoliday: true, reason: '2nd Saturday Off' };
    }
    if (config.fourthSaturdayOff && occurrence === 4) {
      return { isHoliday: true, reason: '4th Saturday Off' };
    }
  }

  return { isHoliday: false };
}

/**
 * Get all holiday dates in a given month for the office.
 * Returns array of { date: string, reason: string }
 */
export function getHolidaysForMonth(
  year: number,
  month: number, // 0-indexed
  config?: OfficeHolidayConfig
): { date: string; reason: string }[] {
  if (!config || !config.enabled) return [];

  const holidays: { date: string; reason: string }[] = [];
  const start = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  for (let day = 1; day <= daysInMonth; day++) {
    const d = new Date(year, month, day);
    const check = isHoliday(d, config);
    if (check.isHoliday) {
      holidays.push({
        date: format(d, 'yyyy-MM-dd'),
        reason: check.reason || 'Holiday',
      });
    }
  }

  return holidays;
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
